import { useMemo } from 'react'
import _ from 'lodash'
import { TierCard } from '@/components/vip-club/TierCard'
import { TierCardSkeleton } from '@/components/vip-club/TierCardSkeleton'
import { StepCard } from '@/components/vip-club/StepCard'
import { CalendarCheckIcon, TierTitleIcon } from '@/components/vip-club/icons'
import { ShieldTickIcon } from '@/components/dashboard/icons'
import { Footer } from '@/components/layout/footer'
import { PageShell } from '@/components/layout/PageShell'
import { LOYALTY_TIER_WORK_STEPS } from '@/config/vip-club'
import { formatRequiredPointsRange, getLoyaltyTierBenefits } from '@/lib/loyalty-benefits'
import { resolvePlayerAssetUrl } from '@/lib/player-api'
import { useGetLoyaltyTiersQuery } from '@/store/api/loyaltyApi'
import { selectLoyaltyTiers } from '@/store/slices/loyaltySlice'
import { useAppSelector } from '@/store/hooks'
import type { ReactNode } from 'react'

const STEP_ICONS: ReactNode[] = [
	<ShieldTickIcon key='tier-title' className='h-6 w-6' />,
	<TierTitleIcon key='points-required' className='h-6 w-6' />,
	..._.times(LOYALTY_TIER_WORK_STEPS.length - 2, (index) => <CalendarCheckIcon key={`benefit-${index}`} className='h-6 w-6' />),
]

export default function VipClub() {
	const { isLoading, isError } = useGetLoyaltyTiersQuery()
	const tiers = useAppSelector(selectLoyaltyTiers)

	const tierCards = useMemo(
		() =>
			_.map(tiers, (tier, index) => ({
				id: tier.id,
				title: tier.tier_name,
				points: formatRequiredPointsRange(tiers, index),
				glowColor: tier.glow_color,
				iconUrl: resolvePlayerAssetUrl(tier.loyalty_icon),
				benefits: getLoyaltyTierBenefits(tier),
			})),
		[tiers],
	)

	return (
		<PageShell gap='10' footer={<Footer />}>
			<div className='flex flex-col items-center gap-3 text-center'>
				<h1 className='text-[32px] font-semibold leading-[136%] tracking-[-0.16px] text-foreground'>Loyalty Clubs</h1>
				<p className='max-w-xl text-base tracking-[-0.24px] text-muted-foreground'>Earn points as you play and unlock exclusive rewards at each tier level.</p>
			</div>

			{isError ? <p className='text-center text-sm text-muted-foreground'>Unable to load loyalty tiers right now.</p> : null}

			<div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
				{isLoading ? _.times(4, (index) => <TierCardSkeleton key={`loyalty-tier-skeleton-${index}`} />) : null}

				{!isLoading && !isError && _.isEmpty(tierCards) ? (
					<p className='col-span-full text-center text-sm text-muted-foreground'>No loyalty tiers are available right now.</p>
				) : null}

				{!isLoading && !isError
					? _.map(tierCards, (tier) => (
							<TierCard key={tier.id} title={tier.title} points={tier.points} glowColor={tier.glowColor} iconUrl={tier.iconUrl} benefits={tier.benefits} />
						))
					: null}
			</div>

			<div className='flex flex-col items-center gap-8'>
				<h2 className='text-center text-[32px] font-semibold leading-[136%] tracking-[-0.16px] text-foreground'>How Loyalty Tiers Work</h2>

				<div className='grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
					{_.map(LOYALTY_TIER_WORK_STEPS, (step, index) => (
						<StepCard key={step.title} step={index + 1} icon={STEP_ICONS[index]} title={step.title} description={step.description} />
					))}
				</div>
			</div>
		</PageShell>
	)
}
